import React, {useEffect, useMemo, useState} from 'react'
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Icon,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack
} from '@chakra-ui/react'
import {MdBlock, MdCancel, MdCheckCircle, MdDelete, MdEdit, MdOutlineError, MdSync, MdVisibility} from 'react-icons/md'
import {HiOutlineDotsVertical} from "react-icons/hi";
import {FaColumns, FaEye, FaFacebook, FaTiktok} from 'react-icons/fa'
import {useNotification} from '../../../contexts/NotificationContext'
import axiosInstance from '../../../api/axiosInstance'
import dayjs from 'dayjs'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Pagination from './Pagination'
import ConfirmationDialog from './ConfirmationDialog'
import UpsellModal from './UpsellModal'
import RiskBadge from './RiskBadge'
import Card from 'components/card/Card'
import {Global} from '@emotion/react'
import {WarningIcon} from '@chakra-ui/icons'
import useHasPermission from '../../../hooks/useHasPermission'
import {normalizePhone} from "../../../Utils/Utils";

const ReportsPage = () => {
    const VISIBLE_COLUMNS_STORAGE_KEY = 'orders_visible_columns';

    const defaultVisibleColumns = {
        id: true,
        risk: true,
        createdAt: true,
        product: true,
        option: true,
        name: true,
        phone: true,
        county: true,
        city: true,
        address: true,
        zip: true,
        total: true,
        status: true,
        synced: true,
        source: true,
        upsell: true,
    };
    const [ordersByDate, setOrdersByDate] = useState([])
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalOrders, setTotalOrders] = useState(0)
    const [products, setProducts] = useState([])
    const [selectedProduct, setSelectedProduct] = useState('')
    const [selectedSource, setSelectedSource] = useState('')
    const [selectedSync, setSelectedSync] = useState('')
    const [selectedRiskLevel, setSelectedRiskLevel] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [shouldSearch, setShouldSearch] = useState(0)
    const [selectedOrders, setSelectedOrders] = useState([])
    const [openDialog, setOpenDialog] = useState(false)
    const [orderToDelete, setOrderToDelete] = useState(null)
    const [openUpsellModal, setOpenUpsellModal] = useState(false)
    const [upsellDetails, setUpsellDetails] = useState([])
    const [upsellAmount, setUpsellAmount] = useState('')
    const {addNotification, addErrorNotifications} = useNotification()
    const [usage, setUsage] = useState([])
    const [blackList, setBlackList] = useState([])
    const [sources, setSources] = useState([])
    const hasPermission = useHasPermission()
    const canDeleteOrders = hasPermission('delete-orders')
    const canEditOrders = hasPermission('edit-orders')
    const isPremium = hasPermission('premium')
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [selectedOrderToEdit, setSelectedOrderToEdit] = useState(null)
    const [editName, setEditName] = useState('')
    const [editPhone, setEditPhone] = useState('')
    const [editAddress, setEditAddress] = useState('')
    const [editCounty, setEditCounty] = useState('')
    const [editCity, setEditCity] = useState('')
    const [editStatus, setEditStatus] = useState('pending')
    const [editOptionId, setEditOptionId] = useState('')
    const [editUpsellDetails, setEditUpsellDetails] = useState([])
    const [postalCodes, setPostalCodes] = useState([])
    const [postalIndex, setPostalIndex] = useState(0)
    const [editPostalCode, setEditPostalCode] = useState('')
    const [aiLoading, setAiLoading] = useState(false)
    const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns)

    useEffect(() => {
        fetchOrdersByDate(currentPage)
    }, [currentPage])

    useEffect(() => {
        fetchOrdersByDate(1)
    }, [startDate, endDate])

    useEffect(() => {
        if (!shouldSearch) return
        fetchOrdersByDate(1)
    }, [shouldSearch])

    useEffect(() => {
        try {
            const stored = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && typeof parsed === 'object') {
                    setVisibleColumns((prev) => ({
                        ...prev,
                        ...parsed,
                    }));
                }
            }
        } catch (e) {
            console.warn('Failed to load visibleColumns from localStorage', e);
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(
                VISIBLE_COLUMNS_STORAGE_KEY,
                JSON.stringify(visibleColumns)
            );
        } catch (e) {
            console.warn('Failed to save visibleColumns to localStorage', e);
        }
    }, [visibleColumns]);

    const fetchOrdersByDate = async (page = currentPage) => {
        try {
            const response = await axiosInstance.get('/reports/orders-by-date', {
                headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
                params: {
                    start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
                    end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
                    page: page,
                    product_id: selectedProduct,
                    searchTerm: searchTerm,
                    source: selectedSource,
                    sync: selectedSync,
                    risk_level: selectedRiskLevel,
                }
            })
            setOrdersByDate(response.data.orders)
            setTotalPages(response.data.total_pages)
            setTotalOrders(response.data.order_count)
            setProducts(response.data.products)
            setSources(response.data.sources)
            await fetchInfoOrders(response.data.orders)
        } catch (error) {
            addErrorNotifications(error.response?.data)
        }
    }

    const handlePageChange = (value) => {
        setCurrentPage(value)
    }

    const handleSearchDatabase = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setShouldSearch(prev => prev + 1);
        }
    };

    const handleSelectOrder = (orderId) => {
        setSelectedOrders((prevSelected) =>
            prevSelected.includes(orderId)
                ? prevSelected.filter((id) => id !== orderId)
                : [...prevSelected, orderId]
        )
    }

    const handleClickOpen = (order) => {
        setOrderToDelete(order)
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
        setOrderToDelete(null)
    }

    const handleConfirmDelete = async () => {
        if (orderToDelete) {
            try {
                await axiosInstance.delete(`/reports/orders/${orderToDelete.id}`, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
                })
                addNotification('Order deleted successfully', 'success')
                fetchOrdersByDate(currentPage)
                handleClose()
            } catch (error) {
                addErrorNotifications(error.response?.data)
            }
        }
    }

    const handleUpsellModalOpen = (upsellDetails, upsellAmount) => {
        setUpsellDetails(upsellDetails)
        setUpsellAmount(upsellAmount)
        setOpenUpsellModal(true)
    }

    const handleBlock = async (order) => {
        await axiosInstance
            .post('/reports/block-ip', {ip: order.ip_address})
            .then(() => addNotification(`${order.ip_address} has been blocked`, 'success'))
    }

    const buildOrderName = (order) => {
        let name = `${order.option_name}`
        const metadata = JSON.parse(order.metadata)
        if (metadata) {
            Object.values(metadata).forEach((meta) => {
                name = name + `<br />${meta.quantity} - ${meta.color}`
            })
        }
        return <div dangerouslySetInnerHTML={{__html: name}}/>
    }

    const handleSelectProduct = async (event) => {
        const value = event.target.value === 'all' ? '' : event.target.value
        setSelectedProduct(value)
        const response = await axiosInstance.get('/reports/orders-by-date', {
            headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            params: {
                start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
                end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
                page: 1,
                product_id: value,
                searchTerm: searchTerm,
                source: selectedSource,
                sync: selectedSync,
                risk_level: selectedRiskLevel,
            }
        })
        setOrdersByDate(response.data.orders)
        setTotalPages(response.data.total_pages)
        setProducts(response.data.products)
        setTotalOrders(response.data.order_count)
        await fetchInfoOrders(response.data.orders)
    }

    const handleSelectSynced = async (event) => {
        const value = event.target.value === 'all' ? '' : event.target.value
        setSelectedSync(value)
        const response = await axiosInstance.get('/reports/orders-by-date', {
            headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            params: {
                start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
                end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
                page: 1,
                product_id: selectedProduct,
                searchTerm: searchTerm,
                source: selectedSource,
                sync: value,
                risk_level: selectedRiskLevel,
            }
        })
        setOrdersByDate(response.data.orders)
        setTotalPages(response.data.total_pages)
        setProducts(response.data.products)
        setSources(response.data.sources)
        setTotalOrders(response.data.order_count)
        await fetchInfoOrders(response.data.orders)
    }

    const handleSelectSource = async (event) => {
        const value = event.target.value === 'all' ? '' : event.target.value
        setSelectedSource(value)
        const response = await axiosInstance.get('/reports/orders-by-date', {
            headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            params: {
                start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
                end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
                page: 1,
                product_id: selectedProduct,
                searchTerm: searchTerm,
                source: value,
                sync: selectedSync,
                risk_level: selectedRiskLevel,
            }
        })
        setOrdersByDate(response.data.orders)
        setTotalPages(response.data.total_pages)
        setProducts(response.data.products)
        setSources(response.data.sources)
        setTotalOrders(response.data.order_count)
        await fetchInfoOrders(response.data.orders)
    }

    const handleSelectRiskLevel = async (event) => {
        const value = event.target.value === 'all' ? '' : event.target.value
        setSelectedRiskLevel(value)
        const response = await axiosInstance.get('/reports/orders-by-date', {
            headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            params: {
                start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
                end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
                page: 1,
                product_id: selectedProduct,
                searchTerm: searchTerm,
                source: selectedSource,
                sync: selectedSync,
                risk_level: value,
            }
        })
        setOrdersByDate(response.data.orders)
        setTotalPages(response.data.total_pages)
        setProducts(response.data.products)
        setSources(response.data.sources)
        setTotalOrders(response.data.order_count)
        await fetchInfoOrders(response.data.orders)
    }

    const fetchInfoOrders = async (orders) => {
        const phones = orders.map((order) => order.phone)
        await axiosInstance
            .post('/reports/get-info', {phones: phones})
            .then((response) => {
                setUsage(response.data.usage)
                setBlackList(response.data.blacklist)
            })
            .catch((error) => {
                console.error('Error fetching phone usage:', error)
            })
    }

    const getOrderCountByPhone = phone => {
        const normalized = normalizePhone(phone)

        const entry = usage.find(
            e => e.normalized_phone === normalized
        )

        return entry ? entry.order_count : 0
    }

    const getOrderBlackListed = (order) => {
        const blacklisted = blackList.find(
            (entry) => entry.order_id === order.id || entry.phone === order.phone
        )
        return !!blacklisted
    }

    const handleDeleteSelected = async () => {
        try {
            await axiosInstance.post(
                '/reports/orders/batch-delete',
                {ids: selectedOrders},
                {
                    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
                }
            )
            fetchOrdersByDate()
            setSelectedOrders([])
            addNotification('Orders deleted successfully', 'success')
        } catch (error) {
            console.error('Error deleting orders:', error)
            addErrorNotifications("Orders couldn't be deleted successfully", 'success')
        }
    }

    const handleSyncSelected = async () => {
        try {
            await axiosInstance.post(
                '/reports/orders/batch-sync',
                {ids: selectedOrders},
                {
                    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
                }
            )
            fetchOrdersByDate()
            setSelectedOrders([])
            addNotification('Orders synced successfully', 'success')
        } catch (error) {
            console.error('Error syncing orders:', error)
            addErrorNotifications("Orders couldn't be synced successfully", 'success')
        }
    }

    const handleSyncSpecific = async (id) => {
        try {
            await axiosInstance.post(
                '/reports/orders/batch-sync',
                {ids: [id]},
                {
                    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
                }
            )
            fetchOrdersByDate()
            setSelectedOrders([])
            addNotification('Order synced successfully', 'success')
        } catch (error) {
            console.error('Error syncing orders:', error)
            addErrorNotifications("Order couldn't be synced successfully", 'success')
        }
    }

    const openEditOrder = (order) => {
        setSelectedOrderToEdit(order)
        setEditName(order.name)
        setEditPhone(order.phone)
        setEditAddress(order.address)
        setEditCounty(order.county || '')
        setEditCity(order.city || '')
        setEditStatus(order.status)
        setEditOptionId(order.option_id || (order.product?.options?.[0]?.id ?? ''))
        setEditUpsellDetails(order.upsell_details || [])
        setIsEditModalOpen(true)

        getPostalCodes(order).then(response => {
            setPostalCodes(response.data)
            setPostalIndex(0)
            setEditPostalCode(order.postal_code || '')
        })
    }

    const handleNextPostal = () => {
        if (!postalCodes.length) return
        setPostalIndex((i) => (i + 3) % postalCodes.length)
    }

    // TODO: this is to be used on the future
    const handleAskAi = () => {
        setAiLoading(true)
        getPostalCodes(selectedOrderToEdit, true).then(response => {
            setPostalCodes(response.data)
            setPostalIndex(0)
            setEditPostalCode(selectedOrderToEdit.postal_code || '')
            setSelectedOrderToEdit(prev => ({
                ...prev,
                ai_count: (prev.ai_count ?? 0) + 1
            }))
        }).finally(() => setAiLoading(false))
    }

    const getPostalCodes = async (order, withAi) => {
        const url = withAi ? '/postal-codes/search-with-ai' : '/postal-codes/search'

        return await axiosInstance.get(url, {
            headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            params: {
                county: order.county,
                city: order.city,
                street: order.address,
                order_id: order.id
            }
        })
    }

    const handleEditSave = async (stay = false) => {
        if (editPhone?.length < 10) {
            addErrorNotifications({error: 'The phone number should have at least 10 digits!'})
            return
        }

        try {
            const payload = {
                name: editName,
                phone: editPhone,
                address: editAddress,
                county: editCounty,
                city: editCity,
                status: editStatus,
                option_id: editOptionId,
                upsell_details: editUpsellDetails,
                postal_code: editPostalCode,
            }
            const order = await axiosInstance.put(`/reports/orders/${selectedOrderToEdit.id}`, payload, {
                headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
            })

            addNotification('Order updated successfully', 'success')
            if (!stay) {
                setIsEditModalOpen(false)
            } else {
                getPostalCodes(order?.data?.order).then(response => {
                    setPostalCodes(response.data)
                    setPostalIndex(0)
                    setEditPostalCode(order?.data?.order?.postal_code || '')
                })
            }
            fetchOrdersByDate(currentPage)
        } catch (error) {
            addErrorNotifications(error.response?.data)
        }
    }

    const visiblePostalCodes = postalCodes.length <= 3
        ? postalCodes
        : [0, 1, 2].map(offset =>
            postalCodes[(postalIndex + offset) % postalCodes.length]
        )

    const handleSetPostalCode = pc => {
        setEditPostalCode(pc.postal_code)
        setEditCity(pc.city)
        setEditCounty(pc.county)
    }

    const digitCount = useMemo(
        () => (editPhone.match(/\d/g) || []).length,
        [editPhone]
    )

    const hasInvalid = useMemo(
        () => /[^0-9]/.test(editPhone) || editPhone?.length < 10 || editPhone?.length > 10,
        [editPhone]
    )

    const toggleColumn = (key) => {
        setVisibleColumns(prev => ({
            ...prev,
            [key]: !prev[key],
        }))
    }

    const getUsageEntryByPhone = (phone) => {
        const normalized = normalizePhone(phone);
        return usage.find((e) => e.normalized_phone === normalized) || null;
    };

    const formatStatusLabel = (s) =>
        String(s || '')
            .replace(/_/g, ' ')
            .replace(/^./, (c) => c.toUpperCase());

    const clearFilters = () => {
        setStartDate(null)
        setEndDate(null)
        setSelectedProduct('')
        setSelectedSource('')
        setSelectedSync('')
        setSelectedRiskLevel('')
    }

    const handleSearchByPhone = (phone) => {
        clearFilters()
        setSearchTerm(phone)
        setCurrentPage(1)
        setShouldSearch((p) => p + 1)
    };

    return (
        <Box pt={{base: '130px', md: '80px', xl: '80px'}} maxWidth="xxl" px={{base: '20px', md: '40px'}}>
            <Card mb="20px" px={{base: 4, md: 6}}>
                <Global
                    styles={`
            .react-datepicker-wrapper {
              display: inherit;
            }
          `}
                />
                <SimpleGrid columns={{base: 1, md: 2, lg: 3, xl: 7}} spacing="20px">
                    <FormControl>
                        <FormLabel>Start Date</FormLabel>
                        <DatePicker dateFormat="dd-MM-yyyy" selected={startDate} onChange={(date) => setStartDate(date)}
                                    customInput={<Input/>}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>End Date</FormLabel>
                        <DatePicker dateFormat="dd-MM-yyyy" selected={endDate} onChange={(date) => setEndDate(date)}
                                    customInput={<Input/>}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Product</FormLabel>
                        <Select value={selectedProduct || 'all'} placeholder="Select Product" onChange={handleSelectProduct}>
                            <option value="all">All Products</option>
                            {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                    {product.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Source</FormLabel>
                        <Select value={selectedSource || 'all'} placeholder="Select Source" onChange={handleSelectSource}>
                            <option value="all">All sources</option>
                            {sources.map((source, index) => (
                                <option key={index} value={source}>
                                    {source.replace(/^./, (char) => char.toUpperCase())}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Filter Synced</FormLabel>
                        <Select value={selectedSync || 'all'} onChange={handleSelectSynced}>
                            <option value="all">All orders</option>
                            <option value={1}>Synced</option>
                            <option value={0}>Not Synced</option>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Risk Level</FormLabel>
                        <Select value={selectedRiskLevel || 'all'} onChange={handleSelectRiskLevel}>
                            <option value="all">All risk levels</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="none">Not scored</option>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Search</FormLabel>
                        <Input
                            type="search"
                            enterKeyHint="search"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearchDatabase}
                        />
                    </FormControl>
                    {selectedOrders.length > 0 && (
                        <Box gridColumn={{base: 'span 1', md: 'span 2'}} textAlign={{base: 'center', md: 'right'}}
                             mt={{base: '20px', md: '0'}}>
                            <Tooltip label="Delete selected orders">
                                <Button colorScheme="red" leftIcon={<Icon as={MdDelete}/>}
                                        onClick={handleDeleteSelected} mr={{base: '0', md: '10px'}}
                                        mb={{base: '10px', md: '0'}} width={{base: 'full', md: 'auto'}}>
                                    Delete
                                </Button>
                            </Tooltip>
                            <Tooltip label="Sync selected orders with woocommerce">
                                <Button colorScheme="blue" leftIcon={<Icon as={MdSync}/>} onClick={handleSyncSelected}
                                        width={{base: 'full', md: 'auto'}}>
                                    Sync
                                </Button>
                            </Tooltip>
                        </Box>
                    )}
                </SimpleGrid>
            </Card>
            <Card>
                <Box overflowX="auto">
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb="4"
                        p="4"
                    >
                        <Text fontSize="2xl" fontWeight="bold">
                            Orders - {totalOrders}
                        </Text>

                        <Menu closeOnSelect={false}>
                            <MenuButton
                                as={IconButton}
                                aria-label="Visible columns"
                                icon={<FaColumns />}
                                size="sm"
                                borderRadius="md"
                                bg="#110675"
                                color="white"
                                _hover={{ bg: "#3C7BC2" }}
                                _active={{ bg: "#3369A3" }}
                                border="1px solid #3C7BC2"
                            />

                            <MenuList minW="230px" maxH="360px" overflowY="auto">
                                <Text px="3" pt="2" pb="1" fontSize="sm" fontWeight="bold">
                                    Toggle columns
                                </Text>

                                <MenuItem onClick={() => toggleColumn('id')}>
                                    <Checkbox
                                        isChecked={visibleColumns.id}
                                        pointerEvents="none"
                                    >
                                        ID
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('risk')}>
                                    <Checkbox
                                        isChecked={visibleColumns.risk}
                                        pointerEvents="none"
                                    >
                                        Risk
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('createdAt')}>
                                    <Checkbox
                                        isChecked={visibleColumns.createdAt}
                                        pointerEvents="none"
                                    >
                                        Created at
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('product')}>
                                    <Checkbox
                                        isChecked={visibleColumns.product}
                                        pointerEvents="none"
                                    >
                                        Product
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('option')}>
                                    <Checkbox
                                        isChecked={visibleColumns.option}
                                        pointerEvents="none"
                                    >
                                        Option
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('name')}>
                                    <Checkbox
                                        isChecked={visibleColumns.name}
                                        pointerEvents="none"
                                    >
                                        Name
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('phone')}>
                                    <Checkbox
                                        isChecked={visibleColumns.phone}
                                        pointerEvents="none"
                                    >
                                        Phone
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('county')}>
                                    <Checkbox
                                        isChecked={visibleColumns.county}
                                        pointerEvents="none"
                                    >
                                        County
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('city')}>
                                    <Checkbox
                                        isChecked={visibleColumns.city}
                                        pointerEvents="none"
                                    >
                                        City
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('address')}>
                                    <Checkbox
                                        isChecked={visibleColumns.address}
                                        pointerEvents="none"
                                    >
                                        Address
                                    </Checkbox>
                                </MenuItem>

                                {isPremium && (
                                    <MenuItem onClick={() => toggleColumn('zip')}>
                                        <Checkbox
                                            isChecked={visibleColumns.zip}
                                            pointerEvents="none"
                                        >
                                            ZIP
                                        </Checkbox>
                                    </MenuItem>
                                )}

                                <MenuItem onClick={() => toggleColumn('total')}>
                                    <Checkbox
                                        isChecked={visibleColumns.total}
                                        pointerEvents="none"
                                    >
                                        Total
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('status')}>
                                    <Checkbox
                                        isChecked={visibleColumns.status}
                                        pointerEvents="none"
                                    >
                                        Status
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('synced')}>
                                    <Checkbox
                                        isChecked={visibleColumns.synced}
                                        pointerEvents="none"
                                    >
                                        Synced
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('source')}>
                                    <Checkbox
                                        isChecked={visibleColumns.source}
                                        pointerEvents="none"
                                    >
                                        Source
                                    </Checkbox>
                                </MenuItem>
                                <MenuItem onClick={() => toggleColumn('upsell')}>
                                    <Checkbox
                                        isChecked={visibleColumns.upsell}
                                        pointerEvents="none"
                                    >
                                        Upsell
                                    </Checkbox>
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </Box>

                    <Table variant="simple" size="sm" minW="100%">
                        <Thead>
                            <Tr>
                                <Th>
                                    <Checkbox
                                        isChecked={selectedOrders.length === ordersByDate.length}
                                        onChange={(e) => setSelectedOrders(e.target.checked ? ordersByDate.map((order) => order.id) : [])}
                                    />
                                </Th>
                                {visibleColumns.id && <Th>ID</Th>}
                                {visibleColumns.risk && <Th>Risk</Th>}
                                {visibleColumns.createdAt && <Th>Created At</Th>}
                                {visibleColumns.product && <Th>Product</Th>}
                                {visibleColumns.option && <Th>Option</Th>}
                                {visibleColumns.name && <Th>Name</Th>}
                                {visibleColumns.phone && <Th>Phone</Th>}
                                {visibleColumns.county && <Th>County</Th>}
                                {visibleColumns.city && <Th>City</Th>}
                                {visibleColumns.address && <Th>Address</Th>}
                                {isPremium && visibleColumns.zip && <Th>ZIP</Th>}
                                {visibleColumns.total && <Th>Total</Th>}
                                {visibleColumns.status && <Th>Status</Th>}
                                {visibleColumns.synced && <Th>Synced</Th>}
                                {visibleColumns.source && <Th>Source</Th>}
                                {visibleColumns.upsell && <Th>Upsell</Th>}
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {ordersByDate.map((order) => (
                                <Tr
                                    key={order.id}
                                    bg={
                                        getOrderBlackListed(order)
                                            ? '#FFCDD2'
                                            : selectedOrders.includes(order.id)
                                                ? '#f7f7f7'
                                                : 'transparent'
                                    }
                                    transition="background-color 0.18s ease"
                                    _hover={{
                                        bg: getOrderBlackListed(order) ? '#F4AEB7' : '#f7f7f7',
                                    }}
                                >
                                    <Td>
                                        <Checkbox isChecked={selectedOrders.includes(order.id)}
                                                  onChange={() => handleSelectOrder(order.id)}/>
                                    </Td>
                                    {visibleColumns.id && <Td>{order.id}</Td>}

                                    {visibleColumns.risk && (
                                        <Td>
                                            <RiskBadge
                                                score={order.risk_score}
                                                level={order.risk_level}
                                                factors={order.risk_factors}
                                                computedAt={order.risk_computed_at}
                                            />
                                        </Td>
                                    )}

                                    {visibleColumns.createdAt && (
                                        <Td>{dayjs(order.created_at).format('DD MMM YYYY HH:mm')}</Td>
                                    )}

                                    {visibleColumns.product && (
                                        <Td>{`${order.quantity} x ${order.product?.name}`}</Td>
                                    )}

                                    {visibleColumns.option && <Td>{buildOrderName(order)}</Td>}

                                    {visibleColumns.name && (
                                        <Td>
                                            <Flex alignItems="center">
                                                <Tooltip label={order.ip_address} aria-label="Block User Tooltip">
                                                    <Text>{order.name}</Text>
                                                </Tooltip>
                                                {order.blocked_ip && (
                                                    <Tooltip label="This IP is blocked" aria-label="Blocked IP Tooltip">
                                                        <Icon as={WarningIcon} color="red.500" ml={2} />
                                                    </Tooltip>
                                                )}
                                            </Flex>
                                        </Td>
                                    )}

                                    {visibleColumns.phone && (
                                        <Td minW={150}>
                                            {(() => {
                                                const count = getOrderCountByPhone(order.phone);

                                                return (
                                                    <div style={{ marginRight: 10 }}>
                                                        {order.phone}{' '}

                                                        {count > 1 && (
                                                            <Tooltip
                                                                label={`This number is found in your orders ${count} times`}
                                                                aria-label="Phone Numbers Duplicates"
                                                            >
                                                                <Badge
                                                                    as="button"
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSearchByPhone(order.phone);
                                                                    }}
                                                                    cursor="pointer"
                                                                    colorScheme="green"
                                                                    borderRadius="full"
                                                                    px="2.5"
                                                                    fontSize="0.8em"
                                                                    _hover={{ filter: 'brightness(0.95)' }}
                                                                    _active={{ transform: 'scale(0.98)' }}
                                                                >
                                                                    {count}
                                                                </Badge>
                                                            </Tooltip>
                                                        )}

                                                        {order.phone?.length < 10 && (
                                                            <Tooltip
                                                                label="This number has less than 10 digits"
                                                                aria-label="Invalid phone tooltip"
                                                            >
                                                                <Icon as={WarningIcon} color="red.500" ml={2} />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </Td>
                                    )}

                                    {visibleColumns.county && <Td>{order.county}</Td>}
                                    {visibleColumns.city && <Td>{order.city}</Td>}
                                    {visibleColumns.address && <Td>{order.address}</Td>}

                                    {isPremium && visibleColumns.zip && (
                                        <Td>{order.postal_code ? order.postal_code : '-'}</Td>
                                    )}

                                    {visibleColumns.total && <Td>{order.total}</Td>}

                                    {visibleColumns.status && (
                                        <Td>
                                            <Text
                                                color={order.status === 'completed' ? 'green.500' : 'orange.500'}
                                            >
                                                {order.status}
                                            </Text>
                                        </Td>
                                    )}

                                    {visibleColumns.synced && (
                                        <Td textAlign="center" verticalAlign="middle">
                                            <Icon
                                                w="24px"
                                                h="24px"
                                                me="5px"
                                                color={order.synced ? 'green.500' : 'red.500'}
                                                as={order.synced ? MdCheckCircle : MdCancel}
                                            />
                                        </Td>
                                    )}

                                    {visibleColumns.source && (
                                        <Td textAlign="center" verticalAlign="middle">
                                            <Icon
                                                w="24px"
                                                h="24px"
                                                me="5px"
                                                color={
                                                    order.source === 'tiktok'
                                                        ? 'black.500'
                                                        : order.source === 'facebook'
                                                            ? 'blue.500'
                                                            : 'gray.500'
                                                }
                                                as={
                                                    order.source === 'tiktok'
                                                        ? FaTiktok
                                                        : order.source === 'facebook'
                                                            ? FaFacebook
                                                            : MdOutlineError
                                                }
                                            />
                                        </Td>
                                    )}

                                    {visibleColumns.upsell && (
                                        <Td textAlign="center" verticalAlign="middle">
                                            {order.upsell_amount > 0 ? (
                                                <IconButton
                                                    backgroundColor="transparent"
                                                    aria-label="view upsell"
                                                    icon={<FaEye />}
                                                    onClick={() =>
                                                        handleUpsellModalOpen(
                                                            order.upsell_details,
                                                            order.upsell_amount
                                                        )
                                                    }
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </Td>
                                    )}
                                    <Td>
                                        <Menu placement="left-start" isLazy>
                                            <MenuButton
                                                as={IconButton}
                                                size="sm"
                                                icon={<HiOutlineDotsVertical/>}
                                                variant="ghost"
                                                aria-label="Actions"
                                            />
                                            <MenuList minW="180px" zIndex={20}>
                                                {canEditOrders && isPremium && !order.synced && (
                                                    <MenuItem
                                                        icon={<MdEdit/>}
                                                        onClick={() => openEditOrder(order)}
                                                    >
                                                        Edit order
                                                    </MenuItem>
                                                )}

                                                {!order.synced ? <MenuItem
                                                    icon={<MdSync/>}
                                                    onClick={() => handleSyncSpecific(order.id)}
                                                >
                                                    Sync
                                                </MenuItem> : null}

                                                {order.upsell_amount > 0 && (
                                                    <MenuItem
                                                        icon={<MdVisibility/>}
                                                        onClick={() =>
                                                            handleUpsellModalOpen(order.upsell_details, order.upsell_amount)
                                                        }
                                                    >
                                                        View upsells
                                                    </MenuItem>
                                                )}

                                                {isPremium && canDeleteOrders && (order.upsell_amount > 0 || (canEditOrders && !order.synced)) &&
                                                    <MenuDivider/>}

                                                {canDeleteOrders && (
                                                    <>
                                                        <MenuItem
                                                            icon={<MdBlock/>}
                                                            color="orange.500"
                                                            onClick={() => handleBlock(order)}
                                                        >
                                                            Block IP
                                                        </MenuItem>
                                                        <MenuItem
                                                            icon={<MdDelete/>}
                                                            color="red.500"
                                                            onClick={() => handleClickOpen(order)}
                                                        >
                                                            Delete order
                                                        </MenuItem>
                                                    </>
                                                )}
                                            </MenuList>
                                        </Menu>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
                <Flex justifyContent="space-between" mt="4" flexWrap="wrap">
                    <Text mb={{base: '8px', md: '0'}}>
                        Showing {Math.min(totalOrders, ordersByDate.length)} from {totalOrders} orders
                    </Text>
                    <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange}/>
                </Flex>
            </Card>
            <UpsellModal open={openUpsellModal} handleClose={() => setOpenUpsellModal(false)}
                         upsellDetails={upsellDetails} upsellAmount={upsellAmount}/>
            <ConfirmationDialog open={openDialog} handleClose={handleClose} handleConfirm={handleConfirmDelete}
                                title="Delete confirmation" description="Are you sure you want to delete this?"/>

            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="6xl">
                <ModalOverlay/>
                <ModalContent maxW="80vw" borderRadius="lg" p={4}>
                    <ModalHeader>Edit Order</ModalHeader>
                    <ModalCloseButton/>

                    <ModalBody>
                        <SimpleGrid columns={{base: 1, md: 2}} spacing={8}>
                            <VStack align="stretch" spacing={4}>
                                <FormControl>
                                    <FormLabel>Name</FormLabel>
                                    <Input value={editName} onChange={e => setEditName(e.target.value)}
                                           focusBorderColor="blue.400"/>
                                </FormControl>

                                <FormControl>
                                    <Box display='flex'>
                                        <FormLabel>Phone <span style={{
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            color: hasInvalid ? '#f56565' : '#48bb78'
                                        }}>({`${digitCount} digits`})</span></FormLabel>
                                    </Box>
                                    <Input value={editPhone} onChange={e => setEditPhone(e.target.value)}
                                           focusBorderColor={hasInvalid ? 'red.400' : 'blue.400'}
                                           borderColor={hasInvalid ? 'red.400' : 'gray.200'}/>
                                </FormControl>

                                {(() => {
                                    const entry = getUsageEntryByPhone(editPhone);
                                    if (!entry || Number(entry.order_count) <= 1) return null;

                                    const statusCounts = entry.status_counts || {};
                                    const hasStatusCounts = statusCounts && Object.keys(statusCounts).length > 0;

                                    return (
                                        <Box
                                            mt={3}
                                            p={4}
                                            borderRadius="xl"
                                            borderWidth="1px"
                                            borderColor="orange.200"
                                            bg="orange.50"
                                            boxShadow="sm"
                                        >
                                            <Flex align="flex-start" gap={3}>
                                                <Icon as={MdOutlineError} boxSize={5} mt="2px" color="orange.500" />
                                                <Box flex="1">
                                                    <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                                                        <Text fontWeight="700">
                                                            This phone number appears in {entry.order_count} orders
                                                        </Text>

                                                        {entry.last_status ? (
                                                            <Badge
                                                                borderRadius="full"
                                                                px="3"
                                                                py="1"
                                                                colorScheme={
                                                                    entry.last_status === 'completed'
                                                                        ? 'green'
                                                                        : entry.last_status === 'cancelled'
                                                                            ? 'red'
                                                                            : 'orange'
                                                                }
                                                            >
                                                                Last order: {formatStatusLabel(entry.last_status)}
                                                            </Badge>
                                                        ) : null}
                                                    </Flex>

                                                    {hasStatusCounts ? (
                                                        <Flex mt={3} gap={2} wrap="wrap">
                                                            {Object.entries(statusCounts)
                                                                .sort((a, b) => Number(b[1]) - Number(a[1]))
                                                                .map(([status, count]) => (
                                                                    <Badge
                                                                        key={status}
                                                                        borderRadius="full"
                                                                        px="3"
                                                                        py="1"
                                                                        variant="subtle"
                                                                        colorScheme={
                                                                            status === 'completed'
                                                                                ? 'green'
                                                                                : status === 'cancelled'
                                                                                    ? 'red'
                                                                                    : 'orange'
                                                                        }
                                                                    >
                                                                        {formatStatusLabel(status)}: {count}
                                                                    </Badge>
                                                                ))}
                                                        </Flex>
                                                    ) : (
                                                        <Text mt={2} fontSize="sm" color="gray.600">
                                                            (No per-status breakdown available.)
                                                        </Text>
                                                    )}
                                                </Box>
                                            </Flex>
                                        </Box>
                                    );
                                })()}

                                <FormControl>
                                    <FormLabel>Address</FormLabel>
                                    <Input value={editAddress} onChange={e => setEditAddress(e.target.value)}
                                           focusBorderColor="blue.400"/>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>City</FormLabel>
                                    <Input value={editCity} onChange={e => setEditCity(e.target.value)}
                                           focusBorderColor="blue.400"/>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>County</FormLabel>
                                    <Input value={editCounty} onChange={e => setEditCounty(e.target.value)}
                                           focusBorderColor="blue.400"/>
                                </FormControl>
                            </VStack>

                            <VStack align="stretch" spacing={4}>
                                <FormControl>
                                    <FormLabel>Postal Code</FormLabel>
                                    <Input value={editPostalCode} onChange={e => setEditPostalCode(e.target.value)}
                                           focusBorderColor="blue.400"/>
                                </FormControl>

                                <Box>
                                    <Box display="flex">
                                        <Text fontWeight="semibold" mb={2}>Postal Code Suggestions</Text>
                                        <Tooltip
                                            label={postalCodes?.length <= 3 ? 'No more codes found for this address' : 'Load next 3 postal codes found'}>
                                            <Button colorScheme="green" isDisabled={postalCodes?.length <= 3}
                                                    onClick={handleNextPostal} icon={<MdSync/>} size="xs" ml="2">Load
                                                more</Button>
                                        </Tooltip>
                                        {/*<Tooltip label={selectedOrderToEdit?.ai_count >= 2 ? 'You have to wait 24 hours before trying again' : 'Try to find the postal code with AI'}>*/}
                                        {/*    <Button isLoading={aiLoading} isDisabled={selectedOrderToEdit?.ai_count >= 2} colorScheme="yellow" onClick={handleAskAi} icon={<MdSync/>} size="xs" ml="2">Ask AI</Button>*/}
                                        {/*</Tooltip>*/}
                                    </Box>
                                    <Flex align="stretch">
                                        {visiblePostalCodes.map(pc => (
                                            <Box
                                                key={pc.id}
                                                border="2px dashed"
                                                borderColor={pc.postal_code === editPostalCode ? 'green.400' : 'red.400'}
                                                borderRadius="md"
                                                p={3}
                                                flex="1"
                                                mx={1}
                                                h="100%"
                                                display="flex"
                                                flexDir="column"
                                                justifyContent="space-between"
                                                cursor="pointer"
                                                _hover={{bg: 'gray.50'}}
                                                onClick={() => handleSetPostalCode(pc)}
                                            >
                                                <Text fontSize="sm" fontWeight="bold">{pc.postal_code}</Text>
                                                <Text fontSize="xs">{pc.street}</Text>
                                                <Text fontSize="xs">{pc.city}</Text>
                                                <Text fontSize="xs">{pc.county}</Text>
                                            </Box>
                                        ))}
                                    </Flex>
                                </Box>

                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <Select value={editStatus} onChange={e => setEditStatus(e.target.value)}
                                            focusBorderColor="blue.400">
                                        <option value="pending">Pending</option>
                                        <option value="cancelled">Cancelled</option>
                                    </Select>
                                </FormControl>

                                {selectedOrderToEdit?.product?.options && (
                                    <FormControl>
                                        <FormLabel>Option</FormLabel>
                                        <Select value={editOptionId} onChange={e => setEditOptionId(e.target.value)}
                                                focusBorderColor="blue.400">
                                            {selectedOrderToEdit.product.options.map(opt => (
                                                <option key={opt.id} value={opt.id}>
                                                    {opt.name} – {opt.reduced_price ? opt.reduced_price : opt.value}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}

                                {editUpsellDetails.length > 0 && (
                                    <Box>
                                        <FormLabel>Upsell Details</FormLabel>
                                        <VStack spacing={2} align="stretch">
                                            {editUpsellDetails.map((upsell, idx) => (
                                                <Flex
                                                    key={idx}
                                                    align="center"
                                                    justify="space-between"
                                                    borderWidth="1px"
                                                    borderColor="gray.200"
                                                    borderRadius="md"
                                                    p={2}
                                                >
                                                    <Text fontSize="sm">{`${upsell.name} – ${upsell.price} RON`}</Text>
                                                    <IconButton
                                                        display="none"
                                                        size="sm"
                                                        variant="ghost"
                                                        icon={<MdDelete/>}
                                                        onClick={() => {
                                                            const next = editUpsellDetails.filter((_, i) => i !== idx)
                                                            setEditUpsellDetails(next)
                                                        }}
                                                        aria-label="Remove upsell"
                                                    />
                                                </Flex>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                            </VStack>
                        </SimpleGrid>
                    </ModalBody>

                    <ModalFooter>
                        <Button size={"sm"} colorScheme="red" mr={3}
                                onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                        <Button size={"sm"} colorScheme="blue" mr={3}
                                onClick={() => handleEditSave(false)}>Save</Button>
                        <Button size={"sm"} colorScheme="yellow" onClick={() => handleEditSave(true)}>Save &
                            Stay</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

        </Box>
    )
}

export default ReportsPage
